import React, { useMemo } from "react";
import { Users, DollarSign, CreditCard, CheckCircle } from "lucide-react";

const StatsCards = ({ payrolls, filterPeriod, filterBudgetCode }) => {
  // We use useMemo to recalculate stats only when data or filter changes
  const stats = useMemo(() => {
    // 1. Filter the data based on the selected Period (YYYY-MM) from the table
    let filteredData = filterPeriod
      ? payrolls.filter((p) => {
          const py = p.payrollPeriod?.year;
          const pm = String(p.payrollPeriod?.month || "").padStart(2, "0");
          return `${py}-${pm}` === filterPeriod;
        })
      : payrolls; // If no period filter, use all data

    // 2. Filter by Budget Code (This logic is correct)
    if (filterBudgetCode) {
        filteredData = filteredData.filter(p => p.budgetCode === filterBudgetCode);
    }    

    const totalRecords = filteredData.length;
    
    // 3. Calculate Payment Stats
    const paidCount = filteredData.filter((p) => p.payment?.status === "paid").length;
    const unpaidCount = totalRecords - paidCount;
    
    // 4. Calculate Financial Stats
    const totalNetSalary = filteredData.reduce(
      (acc, curr) => acc + (curr.netSalary || 0),
      0
    );

    // 5. Calculate Workflow Stats
    const approvedCount = filteredData.filter((p) => p.approvalProcess?.approvedBy).length;
    const checkedCount = filteredData.filter((p) => p.approvalProcess?.checkedBy).length;
    const preparedCount = filteredData.filter((p) => p.approvalProcess?.preparedBy).length;

    return {
      totalRecords,
      paidCount,
      unpaidCount,
      totalNetSalary,
      approvedCount,
      checkedCount,
      preparedCount,
    };
  }, [payrolls, filterPeriod, filterBudgetCode]);

  const formatCurrency = (amount) => {
    return `$${Number(amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Helper to determine the "Next Step" percentage for the workflow card
  const getWorkflowStatus = () => {
    if (stats.totalRecords === 0) return { text: "No Data", color: "text-gray-500", bg: "bg-gray-100" };
    if (stats.approvedCount === stats.totalRecords) return { text: "Fully Approved", color: "text-green-600", bg: "bg-green-100" };
    if (stats.checkedCount === stats.totalRecords) return { text: "Ready for Approval", color: "text-blue-600", bg: "bg-blue-100" };
    if (stats.preparedCount === stats.totalRecords) return { text: "Ready for Check", color: "text-yellow-600", bg: "bg-yellow-100" };
    return { text: "Processing", color: "text-orange-600", bg: "bg-orange-100" };
  };

  const workflowStatus = getWorkflowStatus();

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
      
      {/* Card 1: Total Staff/Records */}
      <div className="bg-white shadow rounded-lg p-5 border-l-4 border-blue-500 flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500 font-medium uppercase tracking-wider">Total Records</p>
          <p className="text-2xl font-bold text-gray-800 mt-1">{stats.totalRecords}</p>
          
          {/* --- NEW CHANGE: Logic to show which filter is active --- */}
          <p className="text-xs text-gray-400 mt-1">
             {filterBudgetCode
               ? `Budget: ${filterBudgetCode}`
               : filterPeriod
               ? "For Selected Period"
               : "All Time"}
          </p>
          {/* -------------------------------------------------- */}
        </div>
        <div className="p-3 bg-blue-50 rounded-full text-blue-500">
          <Users className="w-6 h-6" />
        </div>
      </div>

      {/* Card 2: Total Net Salary */}
      <div className="bg-white shadow rounded-lg p-5 border-l-4 border-indigo-500 flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500 font-medium uppercase tracking-wider">Total Net Salary</p>
          <p className="text-2xl font-bold text-gray-800 mt-1">{formatCurrency(stats.totalNetSalary)}</p>
          <p className="text-xs text-gray-400 mt-1">Liability</p>
        </div>
        <div className="p-3 bg-indigo-50 rounded-full text-indigo-500">
          <DollarSign className="w-6 h-6" />
        </div>
      </div>

      {/* Card 3: Payment Status */}
      <div className="bg-white shadow rounded-lg p-5 border-l-4 border-green-500 flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500 font-medium uppercase tracking-wider">Payment Status</p>
          <div className="flex items-baseline gap-2 mt-1">
            <p className="text-2xl font-bold text-green-600">{stats.paidCount}</p>
            <span className="text-gray-400">/</span>
            <p className="text-xl font-bold text-gray-800">{stats.totalRecords}</p>
          </div>
          <p className="text-xs text-green-600 mt-1 font-semibold">
            {stats.unpaidCount} Unpaid
          </p>
        </div>
        <div className="p-3 bg-green-50 rounded-full text-green-500">
          <CreditCard className="w-6 h-6" />
        </div>
      </div>

      {/* Card 4: Approval Workflow */}
      <div className={`bg-white shadow rounded-lg p-5 border-l-4 border-orange-500 flex items-center justify-between`}>
        <div>
          <p className="text-sm text-gray-500 font-medium uppercase tracking-wider">Workflow Status</p>
          <div className="flex items-baseline gap-2 mt-1">
            <p className="text-2xl font-bold text-gray-800">{stats.approvedCount}</p>
            <span className="text-gray-400">/</span>
            <p className="text-xl font-bold text-gray-800">{stats.totalRecords}</p>
          </div>
          <div className={`mt-1 inline-block px-2 py-0.5 rounded text-xs font-bold ${workflowStatus.color} ${workflowStatus.bg}`}>
            {workflowStatus.text}
          </div>
        </div>
        <div className="p-3 bg-orange-50 rounded-full text-orange-500">
          <CheckCircle className="w-6 h-6" />
        </div>
      </div>

    </div>
  );
};

export default StatsCards;